/**
 * Machine-readable birth-number validation failures (map to copy in the UI / i18n).
 */
export type BirthNumberErrorCode =
	| 'INVALID_FORMAT'
	| 'INVALID_MONTH'
	| 'INVALID_DAY'
	| 'FUTURE_DATE'
	| 'INVALID_CHECK_DIGIT'
	| 'UNKNOWN'

export type BirthNumberValidationResult =
	| { isValid: true; age: number }
	| { isValid: false; errorCode: BirthNumberErrorCode }

/** Two-digit year ≤ this value is treated as 2000–2099; above as 1900–1999 (SK/CZ rodné číslo convention). */
const BIRTH_NUMBER_CENTURY_CUTOFF_YEAR = 23

/**
 * Validates a Slovak/Czech-style birth number (rodné číslo): format, date parts, optional check digit.
 *
 * @param birthNumber - Raw input; spaces and `/` are stripped before validation.
 * @returns Discriminated result: `{ isValid: true, age }` or `{ isValid: false, errorCode }` for UI messaging.
 */
export const validateBirthNumber = (birthNumber: string): BirthNumberValidationResult => {
	try {
		const cleanNumber = birthNumber.replace(/\s/g, '').replace('/', '')

		if (!/^\d{9,10}$/.test(cleanNumber)) {
			return { isValid: false, errorCode: 'INVALID_FORMAT' }
		}

		const year = parseInt(cleanNumber.substring(0, 2), 10)
		let month = parseInt(cleanNumber.substring(2, 4), 10)
		const day = parseInt(cleanNumber.substring(4, 6), 10)

		if (month > 50) {
			month -= 50
		}

		let fullYear: number
		if (year <= BIRTH_NUMBER_CENTURY_CUTOFF_YEAR) {
			fullYear = 2000 + year
		} else {
			fullYear = 1900 + year
		}

		if (month < 1 || month > 12) {
			return { isValid: false, errorCode: 'INVALID_MONTH' }
		}

		if (day < 1 || day > 31) {
			return { isValid: false, errorCode: 'INVALID_DAY' }
		}

		const birthDate = new Date(fullYear, month - 1, day)
		const currentDate = new Date()

		if (birthDate > currentDate) {
			return { isValid: false, errorCode: 'FUTURE_DATE' }
		}

		let age = currentDate.getFullYear() - birthDate.getFullYear()
		const monthDiff = currentDate.getMonth() - birthDate.getMonth()

		if (monthDiff < 0 || (monthDiff === 0 && currentDate.getDate() < birthDate.getDate())) {
			age--
		}

		if (cleanNumber.length === 10) {
			const lastFourDigits = cleanNumber.substring(6, 10)

			if (lastFourDigits === '0000') {
				return { isValid: true, age }
			}

			const controlDigit = parseInt(cleanNumber.substring(9, 10), 10)
			const firstNineDigits = cleanNumber.substring(0, 9)
			const calculatedControl = parseInt(firstNineDigits, 10) % 11

			const expectedControl = calculatedControl === 10 ? 0 : calculatedControl

			if (controlDigit !== expectedControl) {
				return { isValid: false, errorCode: 'INVALID_CHECK_DIGIT' }
			}
		}

		return { isValid: true, age }
	} catch (error) {
		console.error('Error validating birth number:', error)
		return { isValid: false, errorCode: 'UNKNOWN' }
	}
}
